import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import FilterPanel from '@spark/ui-components/components/FilterPanel';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { notification } from '@spark/ui-components/utils/notification';
import {
  getInfracaoMotoristaPremiacaoById,
  createInfracaoMotoristaPremiacao,
  updateInfracaoMotoristaPremiacao,
  deleteInfracaoMotoristaPremiacao
} from '@spark/services/premiacao';
import { infracaoMotoristaFilterConfig } from '../config/infracaoMotoristaFilterConfig';
import { getInfracaoMotoristaColumns } from '../config/infracaoMotoristaColumns';
import {
  emptyInfracaoMotoristaForm,
  infracaoMotoristaFormSections,
  infracaoMotoristaFormStructure,
  validateInfracaoMotoristaForm
} from '../config/infracaoMotoristaFormConfig';
import { usePremiacaoInfracaoMotoristaSelection } from '../hooks/usePremiacaoInfracaoMotoristaSelection';

const pick = (data, ...keys) => {
  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index];
    if (data?.[key] !== undefined && data?.[key] !== null) {
      return data[key];
    }
  }
  return undefined;
};

const extractValue = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'object' && value.value !== undefined) return value.value;
  return value;
};

const parseNumber = (value) => {
  const rawValue = extractValue(value);
  if (rawValue === '' || rawValue === null || rawValue === undefined) return null;
  const parsed = Number(rawValue);
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizeText = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toDigitsOnly = (value) => (value ? String(value).replace(/\D/g, '') : undefined);

const toRowModel = (item) => ({
  id: pick(item, 'id', 'Id'),
  cpfMotorista: pick(item, 'cpfMotorista', 'CpfMotorista') || '',
  mesFechamento: pick(item, 'mesFechamento', 'MesFechamento'),
  anoFechamento: pick(item, 'anoFechamento', 'AnoFechamento'),
  idInfracao: pick(item, 'idInfracao', 'IdInfracao'),
  descricaoInfracao: pick(item, 'descricaoInfracao', 'DescricaoInfracao') || ''
});

const mapApiToFormValues = (data, idInfracao) => ({
  id: pick(data, 'id', 'Id') ?? '',
  cpfMotorista: pick(data, 'cpfMotorista', 'CpfMotorista') || '',
  mesFechamento: pick(data, 'mesFechamento', 'MesFechamento') ?? '',
  anoFechamento: pick(data, 'anoFechamento', 'AnoFechamento') ?? '',
  idInfracao: pick(data, 'idInfracao', 'IdInfracao') ?? idInfracao ?? ''
});

const buildPayload = (values, idInfracao) => ({
  CpfMotorista: toDigitsOnly(values.cpfMotorista) || normalizeText(values.cpfMotorista),
  MesFechamento: parseNumber(values.mesFechamento),
  AnoFechamento: parseNumber(values.anoFechamento),
  IdInfracao: parseNumber(values.idInfracao) ?? parseNumber(idInfracao)
});

const PremiacaoInfracaoMotoristaModal = ({ show, onHide, idInfracao, infracaoContexto }) => {
  const [editingId, setEditingId] = useState(null);
  const [formInitialValues, setFormInitialValues] = useState(emptyInfracaoMotoristaForm);
  const [saving, setSaving] = useState(false);
  const [loadingEditId, setLoadingEditId] = useState(null);
  const [loadingDeleteId, setLoadingDeleteId] = useState(null);
  const {
    motoristas,
    loading,
    paginaAtual,
    quantidadePorPagina,
    totalRegistros,
    handleFiltrosChange,
    handlePaginationChange,
    reload
  } = usePremiacaoInfracaoMotoristaSelection(show, idInfracao);

  useEffect(() => {
    if (!show) {
      setEditingId(null);
      setFormInitialValues(emptyInfracaoMotoristaForm);
      setLoadingEditId(null);
      setLoadingDeleteId(null);
    }
  }, [show]);

  useEffect(() => {
    setEditingId(null);
    setFormInitialValues({
      ...emptyInfracaoMotoristaForm,
      idInfracao: idInfracao || ''
    });
  }, [idInfracao]);

  const handleEdit = useCallback(async (id) => {
    if (!id || loadingEditId === id) return;

    try {
      setLoadingEditId(id);
      const data = await getInfracaoMotoristaPremiacaoById(id);
      setEditingId(id);
      setFormInitialValues(mapApiToFormValues(data, idInfracao));
    } catch (error) {
      notification.error('Erro ao carregar infracao do motorista');
      console.error('Erro ao carregar infracao do motorista:', error);
    } finally {
      setLoadingEditId(null);
    }
  }, [idInfracao, loadingEditId]);

  const handleDelete = useCallback(async (id) => {
    if (!id || loadingDeleteId === id) return;

    try {
      setLoadingDeleteId(id);
      await deleteInfracaoMotoristaPremiacao(id);
      notification.success('Infracao do motorista excluida com sucesso!');
      if (editingId === id) {
        setEditingId(null);
        setFormInitialValues({
          ...emptyInfracaoMotoristaForm,
          idInfracao: idInfracao || ''
        });
      }
      reload();
    } catch (error) {
      notification.error('Erro ao excluir infracao do motorista');
      console.error('Erro ao excluir infracao do motorista:', error);
    } finally {
      setLoadingDeleteId(null);
    }
  }, [editingId, idInfracao, loadingDeleteId, reload]);

  const handleClear = useCallback((form) => {
    setEditingId(null);
    const resetValues = {
      ...emptyInfracaoMotoristaForm,
      idInfracao: idInfracao || ''
    };
    setFormInitialValues(resetValues);
    form.reset(resetValues);
  }, [idInfracao]);

  const handleSubmit = useCallback(async (values) => {
    const payload = buildPayload(values, idInfracao);

    try {
      setSaving(true);
      let data;

      if (values.id) {
        data = await updateInfracaoMotoristaPremiacao(values.id, payload);
        notification.success('Infracao do motorista atualizada com sucesso!');
      } else {
        data = await createInfracaoMotoristaPremiacao(payload);
        notification.success('Infracao do motorista criada com sucesso!');
      }

      setEditingId(pick(data, 'id', 'Id') || values.id || null);
      setFormInitialValues(mapApiToFormValues(data, idInfracao));
      reload();
    } catch (error) {
      notification.error('Erro ao salvar infracao do motorista');
      console.error('Erro ao salvar infracao do motorista:', error);
    } finally {
      setSaving(false);
    }
  }, [idInfracao, reload]);

  const rows = Array.isArray(motoristas) ? motoristas.map(toRowModel) : [];
  const formKey = useMemo(() => (editingId ? `motorista-${editingId}` : 'motorista-create'), [editingId]);

  return (
    <Modal show={show} onHide={onHide} animation={false} size="xl" dialogClassName="modal-uniform-xl">
      <Modal.Header closeButton>
        <Modal.Title>
          Motoristas da Infracao{infracaoContexto ? ` - ${infracaoContexto}` : ''}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <FilterPanel
          filters={infracaoMotoristaFilterConfig}
          onApply={handleFiltrosChange}
          title="Filtros de Motoristas"
          loading={loading}
          defaultCollapsed={false}
        />

        <TanStackTable
          key={`premiacao-infracao-motorista-${paginaAtual}-${quantidadePorPagina}`}
          columns={getInfracaoMotoristaColumns(handleEdit, handleDelete, loadingEditId, loadingDeleteId)}
          data={rows}
          loading={loading}
          withPagination={true}
          pageSizeOptions={[5, 10, 25, 50]}
          enableRowColors={true}
          className="mt-3"
          manualPagination={true}
          totalItems={totalRegistros}
          controlledPagination={{
            pageIndex: Math.max(0, (paginaAtual || 1) - 1),
            pageSize: quantidadePorPagina || 10
          }}
          onPaginationChange={handlePaginationChange}
        />

        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={formInitialValues}
          validate={validateInfracaoMotoristaForm}
        >
          {({ handleSubmit: onFormSubmit, form }) => (
            <FormContainer onSubmit={onFormSubmit} className="mt-3">
              <DynamicForm
                structure={infracaoMotoristaFormStructure}
                sections={infracaoMotoristaFormSections()}
                readOnly={false}
              />

              <FormButtonToolbar className="mt-3">
                <Button variant="secondary" type="button" onClick={() => handleClear(form)} disabled={saving}>
                  Limpar
                </Button>
                <ButtonWithLoading variant="primary" type="submit" loading={saving}>
                  Salvar
                </ButtonWithLoading>
              </FormButtonToolbar>
            </FormContainer>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div className="text-muted">
            {totalRegistros > 0 && (
              <span>
                Exibindo {rows.length} de {totalRegistros} motorista(s)
              </span>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={onHide}>
              Fechar
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PremiacaoInfracaoMotoristaModal;
